import { customerApi } from '@/shared/api/api-customer';
import { useGlobalToast as useToast } from '@/shared/lib/toast';
import {
  Ref,
  ComputedRef,
  ComponentPublicInstance,
  computed,
  toRaw,
  nextTick,
  toRefs,
  WritableComputedRef
} from 'vue';
import { useRouter } from 'vue-router';
import { Control, Form, Nullable, validateIsoDate } from '@/shared';
import { useMortgageModel } from '../mortgage-model';
import { Customer, CustomerFields, Child } from '@/entities/customer';
import { CUSTOMER_SAVE_REQUIRED_HINT, REQUIRED_FIELD_HINT } from '@/shared/constants';
import {
  getMarriageContractDisplayMode,
  MORTGAGE_FIELDS_LABELS,
  SPOUSE_KINSHIP_CODES
} from '@/entities/mortgage/const';

/**
 ** Фабричная функция, которая создает модель для работы с обновлением кастомера
 */
const useMortgageCustomerUpdateModel = (
  customerId: Ref<string | null>,
  customer: ComputedRef<Customer | null>,
  customerType: ComputedRef<'main' | 'customer'>,
  customerRole: WritableComputedRef<string | null>,
  customerCheckPassportId: Ref<number | null>,
  kinshipCoborrower: Control<string | null>,
  landAmount: Control<number | null>,
  sourceDownPayment: Control<string | null>,
  sourceDownPaymentObject: Control<string | null>,
  gazprombankm_outstandingCreditDebt: Control<number | null>,
  form: Form<Partial<Nullable<CustomerFields>>>,
  container: Ref<ComponentPublicInstance | undefined>,
  getCustomerFio: () => string,
  scrollToFirstError: () => void,
  setChildrenFieldErrors: (errors: string[] | null) => void
) => {
  const router = useRouter();
  const { add: addToast } = useToast();
  const mortgageModel = useMortgageModel();
  const {
    mortgage,
    preMortgageData,
    program,
    updatePending,
    updateCustomerPending: updateAppCustomerPending
  } = toRefs(mortgageModel);
  const { post: customerUpdatePost, pending: customerUpdatePending } = customerApi.update();

  const response = computed(() => mortgageModel.updateCustomerResponse);

  const pending = computed(() => {
    if (updatePending.value || customerUpdatePending.value || updateAppCustomerPending.value) {
      return true;
    }

    return false;
  });

  const disabled = computed(() => {
    if (mortgage.value?.stages.current === 'app_issued' || pending.value) {
      return true;
    }

    return false;
  });

  const disabledReason = computed(() => {
    if (mortgage.value?.stages.current === 'app_issued') {
      return 'В завершенных заявках нельзя обновлять анкету';
    }

    if (pending.value) {
      return 'Происходит обновление анкеты';
    }

    return null;
  });

  async function update() {
    const updateCustomerRes = await updateCustomer();
    if (updateCustomerRes?._code === 0 && updateCustomerRes._data) {
      if (customerType.value === 'main') {
        if (
          !landAmount.model.value &&
          (program.value?.bank === 'alfabankbalance' || program.value?.bank === 'alfabankdirect') &&
          (mortgage.value?.real_estate_object === 'HOUSE_WITH_LAND' ||
            preMortgageData.value?.conditions.real_estate_object === 'HOUSE_WITH_LAND')
        ) {
          landAmount.onBlur();
          addToast({
            severity: 'error',
            summary: 'Обновление анкеты',
            detail: REQUIRED_FIELD_HINT('Стоимость земельного участка')
          });
          scrollToFirstError();
          return;
        }

        if (!sourceDownPayment.model.value && program.value?.bank === 'metalinvest') {
          sourceDownPayment.onBlur();
          addToast({
            severity: 'error',
            summary: 'Обновление анкеты',
            detail: REQUIRED_FIELD_HINT('Источник первоначального взноса')
          });
          scrollToFirstError();
          return;
        }

        if (
          program.value?.bank === 'metalinvest' &&
          sourceDownPayment.model.value === 'OTHER' &&
          !sourceDownPaymentObject.model.value
        ) {
          sourceDownPaymentObject.onBlur();
          addToast({
            severity: 'error',
            summary: 'Обновление анкеты',
            detail: REQUIRED_FIELD_HINT('Другой источник первоначального взноса')
          });
          scrollToFirstError();
          return;
        }

        const res = await updateMainCustomer();
        if (res?._code === 0) {
          return updateAppFields();
        } else {
          return res;
        }
      } else {
        if (!kinshipCoborrower.model.value) {
          kinshipCoborrower.onBlur();
          addToast({
            severity: 'error',
            summary: 'Обновление анкеты',
            detail: REQUIRED_FIELD_HINT('Родственная связь с Заёмщиком')
          });
          scrollToFirstError();
          return;
        }

        return updateAppCustomer();
      }
    }
  }

  async function updateAppCustomer() {
    if (customerId.value) {
      const res = await mortgageModel.updateCustomer(
        customerId.value,
        customerRole.value ? customerRole.value : undefined,
        kinshipCoborrower.model.value ? kinshipCoborrower.model.value : undefined,
        customerCheckPassportId.value ? customerCheckPassportId.value : undefined
      );

      if (res?._code === 0 && res._data) {
        addToast({ severity: 'success', summary: `Анкета обновлена`, detail: getCustomerFio() });
      } else {
        addToast({
          severity: 'error',
          summary: `Обновление анкеты`,
          detail: `${getCustomerFio()}. ${res?._message || ''}`.trim()
        });
      }

      return res;
    }
  }

  function updateAppFields() {
    return mortgageModel.update({
      id: mortgage.value?.id,
      gazprombankm_outstandingCreditDebt: gazprombankm_outstandingCreditDebt.model.value,
      land_amount: landAmount.model.value,
      source_down_payment: sourceDownPayment.model.value,
      source_down_payment_object: sourceDownPaymentObject.model.value
    } as any);
  }

  function updateMainCustomer() {
    if (customer.value) {
      return updateAppCustomer();
    } else {
      return create();
    }
  }

  async function create() {
    if (customerId.value) {
      const res = await mortgageModel.create(customerId.value, customerCheckPassportId.value || undefined);

      if (res?._code === 0 && res._data) {
        router.push({ params: { id: res._data.id } });
        addToast({
          severity: 'success',
          summary: 'Первоначальное обновление анкеты',
          detail: 'Появилась возможность добавлять сканы и созаёмщиков!'
        });
      } else {
        addToast({ severity: 'error', summary: 'Первоначальное обновление анкеты', detail: res?._message });
      }
      return res;
    }
  }

  async function updateCustomer() {
    const validationRes = customerValidation();
    if (validationRes) {
      const _data = toRaw(form.values.value) as any;
      _data.citizenship = 'RU';
      _data.id = customerId.value;

      const res = await customerUpdatePost({ _data });

      if (res?._code === 0 && res._data) {
        customerId.value = res._data.id;
        await nextTick();

        form.markAsSaved();

        await form.validation({ setTouched: false });

        const displayModeAfterSave = getMarriageContractDisplayMode(
          program.value?.bank,
          program.value?.mortgage_type
        );
        const marriedAfterSave = form.controls.marital_status?.model === 'MARRIED_OFFICIAL';
        const marriageCtrl = form.controls.marriage_contract;
        const notarialCtrl = form.controls.notarial_consent;
        if (marriedAfterSave && marriageCtrl && notarialCtrl) {
          if (
            (displayModeAfterSave === 'marriage_contract_or_notarial_consent' ||
              displayModeAfterSave === 'family_mortgage_spouse_citizenship_rf') &&
            (marriageCtrl.model || notarialCtrl.model)
          ) {
            marriageCtrl.validation({ setTouched: false });
            notarialCtrl.validation({ setTouched: false });
          } else if (displayModeAfterSave === 'marriage_contract_only' && marriageCtrl.model) {
            marriageCtrl.validation({ setTouched: false });
          }
        }
      } else if (res?._code !== 0) {
        addToast({
          severity: 'error',
          summary: `Обновление анкеты (${getCustomerFio()})`,
          detail: `${getCustomerFio()}. ${res?._message || ''}`.trim()
        });

        if (res?._code === 101 && res._detail) {
          const detail = res._detail as Record<string, string | string[]>;
          const { children: childrenErrors, ...fieldErrors } = detail;

          if (Array.isArray(childrenErrors) && childrenErrors.length) {
            setChildrenFieldErrors(childrenErrors);
          }

          form.setErrors(fieldErrors);
          scrollToFirstError();
        }
      }
      return res;
    } else {
      return null;
    }
  }

  function customerValidation() {
    // Поля, при ошибке в которых сохранение блокируется с отдельным сообщением
    // Чтобы добавить новое поле — достаточно добавить запись в этот массив
    const fieldChecks: Array<{ field: keyof typeof form.controls; errorMessage: string }> = [
      {
        field: 'insurance_number',
        errorMessage: form.controls.insurance_number?.meta.error || 'Ошибка в поле СНИЛС'
      },
      {
        field: 'dateofbirth',
        errorMessage: form.controls.dateofbirth?.meta.error || 'Ошибка в поле Дата рождения'
      },
      {
        field: 'passport_date',
        errorMessage: form.controls.passport_date?.meta.error || 'Ошибка в поле Дата выдачи паспорта'
      }
    ];

    if (program.value?.bank === 'vtbbankm') {
      fieldChecks.push({
        field: 'phone',
        errorMessage: form.controls.phone?.meta.error || 'Ошибка в поле Телефон'
      });
    }

    for (const { field, errorMessage } of fieldChecks) {
      const control = form.controls[field];
      if (!control) continue;
      if (control.model === '' || control.model === null) {
        control.reset();
        continue;
      }
      const valid = control.validation({ setTouched: true });
      if (!valid) {
        addToast({ severity: 'error', summary: 'Обновление анкеты', detail: errorMessage });
        scrollToFirstError();
        return false;
      }
    }

    // Проверяем даты рождения и установки инвалидности в блоке "Дети"
    const childrenList: Child[] = form.controls.children?.model ?? [];

    const hasInvalidChildDate = childrenList.some(child => {
      const birthdayError = child.birthday ? validateIsoDate(child.birthday, { noFutureDate: true }) : null;
      const disabilityDateError = child.disability_date
        ? validateIsoDate(child.disability_date, { noFutureDate: true })
        : null;

      return Boolean(birthdayError || disabilityDateError);
    });

    if (hasInvalidChildDate) {
      addToast({
        severity: 'error',
        summary: 'Обновление анкеты',
        detail: 'Проверьте даты в блоке "Дети": неверный формат или дата из будущего'
      });
      scrollToFirstError();
      return false;
    }

    // Спец-правило: для ВТБ, если тип должности = Моряк,
    // поле "Паспорт-удостоверение моряка" обязательно
    const sailorPassportControl = form.controls.sailor_passport_number;
    const isSailor = form.controls.employment_position_type?.model === 'Sailor';
    const isVtb = program.value?.bank === 'vtbbankm';

    if (isSailor && isVtb && sailorPassportControl) {
      const value = sailorPassportControl.model;
      const isEmpty = value === null || value === undefined || String(value).trim() === '';
      const isValid = sailorPassportControl.validation({ setTouched: true });

      if (isEmpty || !isValid) {
        if (isEmpty) {
          sailorPassportControl.setError('Поле обязательно к заполнению', { setTouched: true });
        }

        addToast({
          severity: 'error',
          summary: 'Обновление анкеты',
          detail: REQUIRED_FIELD_HINT('Паспорт-удостоверение моряка')
        });
        scrollToFirstError();
        return false;
      }
    }

    if (isVtb) {
      const selfEmployeeInnPairs: Array<{
        employmentTypeField: keyof typeof form.controls;
        innField: keyof typeof form.controls;
      }> = [
        { employmentTypeField: 'employment_type', innField: 'work_tax_number' },
        { employmentTypeField: 'add_employment_type', innField: 'add_work_tax_number' }
      ];

      for (const { employmentTypeField, innField } of selfEmployeeInnPairs) {
        if (form.controls[employmentTypeField]?.model !== 'SELF_EMPLOYEE') continue;

        const innControl = form.controls[innField];
        if (!innControl) continue;
        if (innControl.validation({ setTouched: true })) continue;

        addToast({
          severity: 'error',
          summary: 'Обновление анкеты',
          detail: innControl.meta.error || 'ИНН самозанятого содержит 12 цифр'
        });
        scrollToFirstError();
        return false;
      }
    }

    // Спец-правило: для ВТБ, если заполнен блок "Работа по совместительству",
    // поля "Тип занятости" и "Должность" обязательны
    if (isVtb) {
      const addWorkRequiredFields: Array<{ field: 'add_employment_type' | 'add_employment_position'; label: string }> = [
        { field: 'add_employment_type', label: 'Тип занятости' },
        { field: 'add_employment_position', label: 'Должность' }
      ];

      for (const { field, label } of addWorkRequiredFields) {
        const control = form.controls[field];
        if (!control) continue;
        if (control.validation({ setTouched: true })) continue;

        addToast({
          severity: 'error',
          summary: 'Обновление анкеты',
          detail: control.meta.error || REQUIRED_FIELD_HINT(label)
        });
        scrollToFirstError();
        return false;
      }
    }

    const marriageDisplayMode = getMarriageContractDisplayMode(
      program.value?.bank,
      program.value?.mortgage_type
    );
    const marriedOfficial = form.controls.marital_status?.model === 'MARRIED_OFFICIAL';

    const isSpouseKinship = SPOUSE_KINSHIP_CODES.has(kinshipCoborrower.model.value ?? '');
    const hasSpouseKinshipCoborrower = Object.values(mortgage.value?.kinship_coborrower ?? {}).some(k =>
      SPOUSE_KINSHIP_CODES.has(k)
    );
    const spouseCoborrower =
      form.controls.is_spouse_coborrower?.model === true || isSpouseKinship || hasSpouseKinshipCoborrower;
    const partnerIsNotRussianCitizen = form.controls.partner_is_not_russian_citizen?.model === true;
    const isCoborrowerForm = customerType.value === 'customer';
    const shouldSkipMarriageValidation =
      isCoborrowerForm ||
      (!partnerIsNotRussianCitizen && marriageDisplayMode === 'family_mortgage_spouse_citizenship_rf') ||
      (partnerIsNotRussianCitizen && marriageDisplayMode !== 'family_mortgage_spouse_citizenship_rf') ||
      (spouseCoborrower && marriageDisplayMode !== 'family_mortgage_spouse_citizenship_rf');
    if (shouldSkipMarriageValidation) {
      form.controls.marriage_contract?.validation({ setTouched: false });
      form.controls.notarial_consent?.validation({ setTouched: false });
    } else if (
      marriedOfficial &&
      partnerIsNotRussianCitizen &&
      marriageDisplayMode === 'family_mortgage_spouse_citizenship_rf'
    ) {
      const marriageContractControl = form.controls.marriage_contract;
      const notarialConsentControl = form.controls.notarial_consent;

      const hasMarriage = Boolean(marriageContractControl?.model);
      const hasNotarial = Boolean(notarialConsentControl?.model);
      if (!hasMarriage && !hasNotarial) {
        const msg = 'Выберите «Брачный договор» или «Нотариальное согласие»';
        marriageContractControl?.setError(msg, { setTouched: true });
        notarialConsentControl?.setError(msg, { setTouched: true });
        addToast({ severity: 'error', summary: 'Обновление анкеты', detail: msg });
        scrollToFirstError();
        return false;
      }
    }

    // Обязательные базовые поля
    const requiredFields: Array<keyof typeof form.controls> = [
      'last_name',
      'first_name',
      'passport_serial',
      'passport_number'
    ];

    const requiredValid = requiredFields.every(field => {
      const control = form.controls[field];
      return control ? control.validation({ setTouched: true }) : true;
    });

    if (!requiredValid) {
      addToast({
        severity: 'error',
        summary: 'Обновление анкеты',
        detail: CUSTOMER_SAVE_REQUIRED_HINT
      });
      scrollToFirstError();
      return false;
    }

    if (customerType.value === 'customer') {
      const coborrowerRequiredFields: Array<{
        field: 'employer_activity' | 'employer_legal_form';
      }> = [{ field: 'employer_activity' }, { field: 'employer_legal_form' }];

      const invalidCoborrowerFields: string[] = [];

      for (const { field } of coborrowerRequiredFields) {
        const control = form.controls[field];

        if (!control) continue;

        const valid = control.validation({ setTouched: true });

        if (!valid) {
          invalidCoborrowerFields.push(MORTGAGE_FIELDS_LABELS[field] || String(field));
        }
      }

      if (invalidCoborrowerFields.length) {
        addToast({
          severity: 'error',
          summary: 'Обновление анкеты',
          detail: REQUIRED_FIELD_HINT(invalidCoborrowerFields.join(', '))
        });
        scrollToFirstError();

        return false;
      }
    }

    return true;
  }

  return { update, pending, disabled, disabledReason, container, response };
};

export default useMortgageCustomerUpdateModel;
